import { useApiClient } from "@albertine/api-client";
import type { Membership } from "@albertine/types";
import {
    type ListMembersQuery,
    type MemberListItem,
    SuccessResponseListMembersSchema,
} from "@albertine/types/openapi/lmt";
import { useInfiniteQuery } from "@tanstack/react-query";

export type PagedMemberListItem = Omit<MemberListItem, "membership"> & {
    membership: Membership | null;
};

export function usePagedMembers(query: Omit<ListMembersQuery, "continuationToken">) {
    const { apiClient } = useApiClient();

    const membersQuery = useInfiniteQuery({
        queryKey: ["listMembers", query],
        queryFn: async ({ pageParam }) => {
            const response = await apiClient.get.listMembers({
                query: {
                    continuationToken: pageParam || undefined,
                    pageSize: query.pageSize ? String(query.pageSize) : undefined,
                    memberGroup: query.memberGroup,
                    sortBy: query.sortBy,
                    sortOrder: query.sortOrder,
                    search: query.search,
                    groups: query.groups ?? [],
                    engagement: query.engagement ?? [],
                    assignedTo: query.assignedTo ?? [],
                    teamIds: query.teamIds ?? [],
                    requests: query.requests ?? [],
                    agentEngagement: query.agentEngagement ?? [],
                    membershipTiers: query.membershipTiers ?? [],
                    membershipStages: query.membershipStages ?? [],
                },
            });
            const data = await response.json();

            if (!data.success) {
                throw data;
            }

            return SuccessResponseListMembersSchema.parse(data).data;
        },
        getNextPageParam: (lastPage) => {
            return lastPage.continuationToken ?? undefined;
        },
        initialPageParam: "",
    });

    const firstPage = membersQuery.data?.pages[0];
    const members =
        (membersQuery.data?.pages.flatMap((page) => page.items) as
            | PagedMemberListItem[]
            | undefined) ?? [];

    return {
        members,
        total: firstPage?.total ?? 0,
        facets: firstPage?.facets,
        isLoading: membersQuery.isLoading,
        isFetchingNextPage: membersQuery.isFetchingNextPage,
        hasNextPage: membersQuery.hasNextPage,
        fetchNextPage: membersQuery.fetchNextPage,
    };
}
